export const getCompanyPath = () => {
  return window.location.pathname.split("/")[1];
}

export const base64ToBlob = (base64Data: string): Blob => {
  const byteString = atob(base64Data.split(',')[1]);
  const mimeType = base64Data.split(',')[0].split(':')[1].split(';')[0];
  const byteNumbers = new Array(byteString.length).fill(0).map((_, i) => byteString.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};


export const getFileSize = (size: number) => {
  if(size < 1024 * 1024) {
    return Math.round(size / 1024) + 'KB';
  }
  
  return Math.round(size / 1024 / 1024) + 'MB';
}

export const popupPdfViewer = (pdfId: number) => {
  window.open(`/${getCompanyPath()}/pdf-viewer?pdf_id=${pdfId}`, '_blank', 'fullscreen=yes,width='+screen.availWidth+',height='+screen.availHeight);
}

export const removeSpaces = (str: string) => {
  return str.replace(/\s/g, '');
}


/**
 * 히스토리가 있으면 뒤로 가고, 없으면 창을 닫는 함수
 * @param navigate React Router의 navigate 함수
 * @param onClose 창 닫기 콜백 함수 (선택적)
 */
export const navigateBackOrClose = (navigate: any, onClose?: () => void) => {
  // React Router의 navigation state에 이전 앱 내부 경로가 있으면 뒤로 이동.
  // window.history.length는 외부 링크(이메일 등)로 직접 접근했을 때도 2 이상이므로
  // 신뢰도가 낮다. history.state.idx가 0이면 앱 내 첫 진입(=뒤로 갈 앱 내 경로 없음)으로 판단.
  const historyIdx: number = (window.history.state as { idx?: number } | null)?.idx ?? -1;
  const canGoBack = historyIdx > 0;

  if (!canGoBack) {
    if (onClose) {
      onClose();
    } else {
      window.close();
      try {
        if (!window.closed) {
          alert('브라우저 정책으로 인해 창을 자동으로 닫을 수 없습니다.\n창을 직접 닫아주세요.');
        }
      } catch (e) {
        // ignore
      }
    }
  } else {
    navigate(-1);
  }
};


/**
 * 기업 이름에서 법인 표시 및 특수문자를 제거하는 함수
 * @param companyName 기업 이름
 * @returns 법인 표시 및 특수문자가 제거된 기업 이름
 */
export const cleanCompanyName = (companyName: string): string => {
  if (!companyName) return '';
  
  // 정규식을 사용하여 다양한 형태의 법인 표시 제거
  // (주), (주식회사), 주식회사, (유), (유한회사), 유한회사 등의 패턴 제거
  let cleanedName = companyName
    .replace(/(\(주\)|\(주식회사\)|\(유\)|\(유한회사\))/g, '')  // 괄호 있는 형태
    .replace(/(^주식회사|^㈜|^(주)|^유한회사)/g, '')  // 문자열 시작 부분
    .replace(/(\s+주식회사|\s+㈜|\s+(주)|\s+유한회사)$/g, '');  // 문자열 끝 부분
  
  // 앞뒤 공백 제거
  cleanedName = cleanedName.trim();
  
  return cleanedName;
};


/**
 * 생년월일과 성별을 받아서 생년월일-성별 형식으로 반환하는 함수
 * @param birth 생년월일
 * @param gender 성별
 * @returns 생년월일-성별 형식의 문자열
 */
export const getBirthGender = (birth: string, gender: string) => {
  if(!birth || !gender) return '';

  const year = birth.slice(0, 4);
  const shortBirth = birth.slice(2); // YYMMDD
  let genderCode = '';
  if (parseInt(year, 10) >= 2000) {
    genderCode = gender === 'M' ? '3' : '4';
  } else {
    genderCode = gender === 'M' ? '1' : '2';
  }
  return `${shortBirth}-${genderCode}`;
}


// 아이디 유효성 검사
export const validateLoginId = (loginId: string): string | null => {
  if (!loginId.trim()) {
    return '아이디를 입력해주세요.';
  }
  if (loginId.length < 4) {
    return '아이디는 4자 이상이어야 합니다.';
  }
  if (loginId.length > 20) {
    return '아이디는 20자 이하여야 합니다.';
  }
  
  const hasLetter = /[a-zA-Z]/.test(loginId);
  const isValidFormat = /^[a-zA-Z0-9]+$/.test(loginId);
  
  if (!isValidFormat) {
    return '아이디는 영문과 숫자만 사용할 수 있습니다.';
  }
  if (!hasLetter) {
    return '아이디는 영문을 반드시 포함해야 합니다.';
  }
  
  return null;
}

// 비밀번호 유효성 검사
export const validatePassword = (password: string): string | null => {
  const length = password.length >= 8;
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    
    const validTypes = [hasLetter, hasNumber, hasSpecial].filter(Boolean).length;
    const isValid = length && validTypes >= 2;
    if(isValid) {
      return null;
    }
    
    return '비밀번호는 8자 이상이어야 하며, 영문, 숫자, 특수문자 중 2가지 이상 조합이어야 합니다.';
};


/**
 * 직원 목록을 우선순위에 따라 정렬합니다.
 * 우선순위: 대표이사 > 핵심인력 > 일반 직원
 * 각 그룹 내에서는 입사일 오름차순 정렬 (먼저 입사한 사람이 위로)
 * 
 * @param employees 정렬할 직원 목록
 * @returns 정렬된 직원 목록 (원본 배열을 변경하지 않음)
 */
export const sortEmployeesByPriority = <T extends { 
  isRepresentative?: boolean; 
  isCoreOperator?: boolean; 
  joinedAt?: string;
}>(employees: T[]): T[] => {
  return [...employees].sort((a, b) => {
    // 1. 대표이사 우선
    if (a.isRepresentative && !b.isRepresentative) return -1;
    if (!a.isRepresentative && b.isRepresentative) return 1;
    
    // 2. 핵심인력 우선
    if (a.isCoreOperator && !b.isCoreOperator) return -1;
    if (!a.isCoreOperator && b.isCoreOperator) return 1;
    
    // 3. 입사일 오름차순 (먼저 입사한 사람이 위로)
    const dateA = a.joinedAt ? new Date(a.joinedAt).getTime() : Infinity;
    const dateB = b.joinedAt ? new Date(b.joinedAt).getTime() : Infinity;
    return dateA - dateB;
  });
};